import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from '../../../store/reducers';
import './MissionsModal.css';
import { TabPanel, TabPanelTab } from '../../common/TabPanel';
import { translate } from 'game/translate';
import { DailyMissions } from './components/DailyMissions';
import { GAEvent, GAEventNames } from 'game/analytics/events';

export const MissionsModal: React.FC = () => {
  const dispatch = useDispatch();
  const { missionsModal } = useSelector((state: UIRootState) => state.gameUI.modals);
  const isOpen = missionsModal.isOpen;

  const [panelVisible, setPanelVisible] = useState<boolean>(false);
  const [panelClosing, setPanelClosing] = useState<boolean>(false);
  const [infoPanelVisible, setInfoPanelVisible] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>('daily_mission');

  useEffect(() => {
    console.log('isOpen', isOpen);
    if (isOpen) {
      setPanelVisible(true);
      setPanelClosing(true);
      setInfoPanelVisible(false);
      const t = setTimeout(() => setPanelClosing(false), 0);
      return () => clearTimeout(t);
    } else if (panelVisible) {
      setPanelClosing(true);
      const t = setTimeout(() => {
        setPanelVisible(false);
        setPanelClosing(false);
        setInfoPanelVisible(false);
      }, 360);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const tabsConfig: Array<TabPanelTab & { key: string }> = useMemo(() => [
    {
      key: 'daily_mission',
      title: translate('daily_mission_title'),
      disabled: false,
      visible: true,
      content: <DailyMissions />
    },
    {
      key: 'weekly_mission',
      title: translate('weekly_mission_title'),
      disabled: true,
      content: <></>
    },
  ], []);

  const currentTab = useMemo(() => tabsConfig.find(t => t.key === activeTab), [activeTab]);
  const firstEnabled = useMemo(() => tabsConfig.find(t => !t.disabled)?.key || 'daily_mission', [tabsConfig]);
  const normalizedActiveTab: string = useMemo(() => currentTab && !currentTab.disabled ? currentTab.key : firstEnabled, [currentTab, firstEnabled]);

  useEffect(() => {
    if (activeTab !== normalizedActiveTab) {
      setActiveTab(normalizedActiveTab);
    }
  }, [normalizedActiveTab, activeTab, dispatch]);

  const contentKey = `${normalizedActiveTab}-${missionsModal.isOpen ? 'open' : 'closed'}`;

  const handleButtonClick = (buttonKey: string, data?: any) => {
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleInfoPanelClick = useCallback(() => {
    GAEvent(GAEventNames.MISSION_INFO_BUTTON_CLICK);
    setInfoPanelVisible(true);
  }, []);

  const handleCloseMissionsButtonClick = useCallback(() => {
    GAEvent(GAEventNames.MISSION_CLOSE_BUTTON_CLICK);
    handleButtonClick('close-missions');
  }, []);

  const tabsVariantClass = 'design-tabs--two';

  return (
    panelVisible ? (
      <div 
        id="game-ui-design" 
        className={`design-modal text-white flex-column h-100 w-100 panel--open ${panelClosing ? 'panel--closing' : ''}`}
      >
        {infoPanelVisible && (
          <div className="info-panel">
            <div id="closing-ignore-el" className="info-panel-back-btn" onClick={() => setInfoPanelVisible(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <g opacity="0.6">
                <path d="M19 23L12 16L19 9" stroke="white" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
              </svg>
            </div>
            <span className="info-panel-title">
              {translate('how_to_title')}
            </span>
            <div className="info-panel-divider"/>

            <div className="info-panel-content">
              <div className="info-panel-point-wrapper">
                <div className="info-panel-point">
                  <span style={{paddingRight: '2px'}} className="info-panel-point-number">1</span>
                  <div className="info-panel-point-line"/>
                </div>
                <picture>
                  <source srcSet="/assets/missions/mission_step_1.webp" type="image/webp" />
                  <img style={{width: '116px', height: '88px'}} src="/assets/missions/mission_step_1.png"/>
                </picture>
                <span style={{marginTop: '7px'}} className="info-panel-point-text">
                  {translate('mission_guide_step_1')}
                </span>
              </div> 

              <div className="info-panel-point-wrapper">
                <div className="info-panel-point" style={{marginTop: '10px'}}>
                  <span className="info-panel-point-number" style={{marginRight: 0}}>2</span>
                  <div className="info-panel-point-line"/>
                </div>
                <picture style={{position: 'relative', bottom: '23px'}}>
                    <source srcSet="/assets/missions/mission_step_2.webp" type="image/webp" />
                    <img style={{width: '116px', height: '88px'}} src="/assets/missions/mission_step_2.png"/>
                </picture>
                <span className="info-panel-point-text" style={{height: '42px', marginLeft: '11px'}}>
                  {translate('mission_guide_step_2')}
                </span>
              </div>    

              <div className="info-panel-point-wrapper">
                <div className="info-panel-point" style={{marginTop: '10px'}}>
                  <span className="info-panel-point-number" style={{marginRight: 0}}>3</span>
                </div>
                <picture style={{position: 'absolute', marginBottom: '20px', marginTop: '-34px', paddingLeft: '20px', paddingBottom: '-19px', paddingTop: '-16px'}}>
                    <source srcSet="/assets/missions/lights.webp" type="image/webp" />
                    <img src="/assets/missions/lights.png"/>
                </picture>
                <picture style={{position: 'relative', bottom: '23px'}}>
                    <source srcSet="/assets/missions/mission_step_3.webp" type="image/webp" />
                    <img style={{width: '116px', height: '88px'}} src="/assets/missions/mission_step_3.png"/>
                </picture>
                <span className="info-panel-point-text" style={{height: '42px', marginLeft: '11px'}}>
                  {translate('mission_guide_step_3')}
                </span>
              </div>           
            </div>
          </div>
        )}
        {!infoPanelVisible && (
        <div className="missions-content-wrapper">
        <div className="design-header d-flex justify-content-between align-items-center mb-4">
          <div className="design-header-left d-flex align-items-center">
            <span id="design-header" className="design-title mb-0 text-white">{translate('missions_title')}</span>
            <div className="mission-info-icon" id="closing-ignore-el" onClick={handleInfoPanelClick}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <g opacity="0.6">
                <g filter="url(#filter0_d_9605_25581)">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.9977 25.0033C20.9692 25.0033 24.9993 20.9731 24.9993 16.0016C24.9993 11.0302 20.9692 7 15.9977 7C11.0263 7 6.99609 11.0302 6.99609 16.0016C6.99609 20.9731 11.0263 25.0033 15.9977 25.0033ZM15.0852 13.3505V21.5323H17.3544V13.3505H15.0852ZM15.3569 11.9602C15.5984 12.1839 15.8878 12.2958 16.2251 12.2958C16.5625 12.2958 16.8501 12.1839 17.0881 11.9602C17.3295 11.733 17.4503 11.4613 17.4503 11.1452C17.4503 10.8327 17.3295 10.5646 17.0881 10.3409C16.8501 10.1136 16.5625 10 16.2251 10C15.8878 10 15.5984 10.1136 15.3569 10.3409C15.119 10.5646 15 10.8327 15 11.1452C15 11.4613 15.119 11.733 15.3569 11.9602Z" fill="white"/>
                </g>
                </g>
                <defs>
                <filter id="filter0_d_9605_25581" x="6.99609" y="7" width="18.0039" height="18.8746" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.870654"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_9605_25581"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_9605_25581" result="shape"/>
                </filter>
                </defs>
              </svg>
            </div>
          </div>
          <button 
            id="close-design-button"
            className="btn btn-link text-white p-0 border-0 design-close"
            onClick={handleCloseMissionsButtonClick}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
            </svg>
          </button>
        </div>

      <TabPanel
        tabs={tabsConfig}
        activeKey={normalizedActiveTab}
        onChange={(key) => handleTabClick(key)}
        tabsBarClassName={`design-tabs d-flex rounded-pill p-1 position-relative ${tabsVariantClass}`}
        activeIndexClassPrefix="design-tabs--active-"
        tabButtonClassName="modal-tab"
        showIndicator
        contentWrapperClassName={`design-content flex-grow-1 design-content--no-padding overflow-hidden mission-content w-100`}
        contentKey={contentKey}
        disabledBtnClassName="modal-tab--disabled"
      />
      </div>
        )}
      </div>
    ) : null
  );
}; 